import type { DishAnalysis } from '@/common/generated/wine-dish-analysis.typebox';
import { Card } from '@/pages/live-chat/components/card';

interface DishAnalysisCardProps {
  analysis: DishAnalysis;
  className?: string;
}

export function DishAnalysisCard({
  analysis,
  className = '',
}: DishAnalysisCardProps) {
  return (
    <Card
      className={`p-4 border-2 border-primary/30 shadow-md relative ${className}`}
    >
      <div className="absolute -top-3 left-4 bg-blue-100 text-blue-700 px-2 py-0.5 text-sm rounded-full font-medium">
        Dish Analysis
      </div>
      <div className="space-y-4 pt-2">
        <div>
          <h3 className="text-lg font-semibold">{analysis.name}</h3>
          {analysis.cuisine && (
            <p className="text-sm text-muted-foreground">
              Cuisine: {analysis.cuisine}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-2">Key Characteristics</h4>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">Intensity:</span>{' '}
                <span className="capitalize">{analysis.overallIntensity}</span>
              </p>
              <p className="text-sm">
                <span className="font-medium">Temperature:</span>{' '}
                <span className="capitalize">
                  {analysis.textureProfile.temperature}
                </span>
              </p>
              {analysis.pairingConsiderations.regionalSuggestions &&
                analysis.pairingConsiderations.regionalSuggestions.length >
                  0 && (
                  <div className="text-sm">
                    <span className="font-medium">Regional Suggestions:</span>
                    <ul className="list-disc list-inside ml-2">
                      {analysis.pairingConsiderations.regionalSuggestions.map(
                        (region) => (
                          <li key={region} className="capitalize">
                            {region}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Ingredients & Methods</h4>
            <div className="space-y-2">
              {analysis.ingredients.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium">Main Ingredients:</span>
                  <ul className="list-disc list-inside ml-2">
                    {analysis.ingredients.map((ingredient) => (
                      <li key={ingredient.name} className="capitalize">
                        {ingredient.name} ({ingredient.role},{' '}
                        {ingredient.intensity})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.cookingMethods.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium">Cooking Methods:</span>
                  <ul className="list-disc list-inside ml-2">
                    {analysis.cookingMethods.map((method) => (
                      <li key={method.technique} className="capitalize">
                        {method.technique} ({method.duration}
                        {method.temperature ? `, ${method.temperature}` : ''})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium">Flavor Profile</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.flavorProfile.dominant.length > 0 && (
              <div className="text-sm">
                <span className="font-medium">Primary Flavors:</span>
                <ul className="list-disc list-inside ml-2">
                  {analysis.flavorProfile.dominant.map((flavor) => (
                    <li key={flavor} className="capitalize">
                      {flavor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.flavorProfile.secondary.length > 0 && (
              <div className="text-sm">
                <span className="font-medium">Secondary Flavors:</span>
                <ul className="list-disc list-inside ml-2">
                  {analysis.flavorProfile.secondary.map((flavor) => (
                    <li key={flavor} className="capitalize">
                      {flavor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {analysis.textureProfile.primary.length > 0 && (
          <div className="text-sm">
            <span className="font-medium">Texture Notes:</span>
            <ul className="list-disc list-inside ml-2">
              {analysis.textureProfile.primary.map((texture) => (
                <li key={texture} className="capitalize">
                  {texture}
                </li>
              ))}
            </ul>
          </div>
        )}

        {analysis.pairingConsiderations.complementaryFlavors.length > 0 && (
          <div className="text-sm">
            <span className="font-medium">Complementary Flavors:</span>
            <ul className="list-disc list-inside ml-2">
              {analysis.pairingConsiderations.complementaryFlavors.map(
                (flavor) => (
                  <li key={flavor} className="capitalize">
                    {flavor}
                  </li>
                ),
              )}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
